/**
 * Lock follows the OS (docs/security/passkey-budget.md §3 P1, WP1).
 *
 * A browser on the same computer as an enrolled vikod may clear its APP LOCK without a passkey
 * when the computer's own login proved the user is back: vikod saw the OS screen lock and then
 * unlock (Touch ID / password) after the moment the browser locked. vikod signs that fact with its
 * pinned daemon identity key over a nonce the browser chose, bound to the browser's device key.
 *
 * The proof never satisfies a risky command, never releases content keys, and lives ≤ 60 s.
 * Verification uses only the browser's own enrollment pin (never a relay-supplied key).
 */
import { fromBase64Url, randomBytes, toBase64Url } from './bytes.js';
import { signStatement, verifyStatement } from './daemon-identity.js';
export const PRESENCE_UNLOCK_DOMAIN = 'viko/presence-unlock/v1';
export const PRESENCE_UNLOCK_MAX_TTL_MS = 60_000;
const CLOCK_SKEW_MS = 30_000;
export const newPresenceNonce = () => toBase64Url(randomBytes(32));
/** vikod: sign that the console session unlocked after `osLockedAt`. */
export async function signPresenceUnlock(signer, i) {
    const statement = {
        v: 1,
        type: 'viko.presence-unlock',
        accountId: i.accountId,
        machineId: i.machineId,
        daemonKeyId: signer.keyId,
        deviceKeyId: i.deviceKeyId,
        nonce: i.nonce,
        osLockedAt: i.osLockedAt,
        osUnlockedAt: i.osUnlockedAt,
        issuedAt: i.now,
        expiresAt: i.now + Math.min(i.ttlMs ?? PRESENCE_UNLOCK_MAX_TTL_MS, PRESENCE_UNLOCK_MAX_TTL_MS),
    };
    return { statement, signature: await signStatement(signer, PRESENCE_UNLOCK_DOMAIN, statement) };
}
const KEYS = [
    'accountId',
    'daemonKeyId',
    'deviceKeyId',
    'expiresAt',
    'issuedAt',
    'machineId',
    'nonce',
    'osLockedAt',
    'osUnlockedAt',
    'type',
    'v',
];
const isTime = (v) => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
function wellFormed(p) {
    const o = p;
    const s = o?.statement;
    if (!s || typeof o.signature !== 'string')
        return false;
    if (Object.keys(s).sort().join() !== KEYS.join())
        return false;
    if (s.v !== 1 || s.type !== 'viko.presence-unlock')
        return false;
    for (const k of ['accountId', 'machineId', 'daemonKeyId', 'deviceKeyId', 'nonce'])
        if (typeof s[k] !== 'string' || !s[k].length)
            return false;
    try {
        if (fromBase64Url(s.nonce).length !== 32)
            return false;
    }
    catch {
        return false;
    }
    return ['osLockedAt', 'osUnlockedAt', 'issuedAt', 'expiresAt'].every((k) => isTime(s[k]));
}
/**
 * Browser: verify a relayed proof against ITS enrollment pin and ITS own expectations.
 * `since` is the earliest OS lock that counts (see `presenceSince` in the web app lock).
 */
export async function verifyPresenceUnlock(proof, pin, expect) {
    if (!wellFormed(proof))
        return { ok: false, reason: 'malformed' };
    const s = proof.statement;
    if (s.accountId !== expect.accountId)
        return { ok: false, reason: 'wrong-account' };
    if (s.machineId !== expect.machineId)
        return { ok: false, reason: 'wrong-machine' };
    if (s.daemonKeyId !== pin.daemonKeyId)
        return { ok: false, reason: 'wrong-daemon-key' };
    if (s.deviceKeyId !== expect.deviceKeyId)
        return { ok: false, reason: 'wrong-device' };
    if (s.nonce !== expect.nonce)
        return { ok: false, reason: 'wrong-nonce' };
    if (s.osLockedAt < expect.since)
        return { ok: false, reason: 'lock-too-old' };
    if (s.osUnlockedAt < s.osLockedAt || s.osUnlockedAt > s.issuedAt)
        return { ok: false, reason: 'not-unlocked-after-lock' };
    if (s.expiresAt - s.issuedAt > PRESENCE_UNLOCK_MAX_TTL_MS)
        return { ok: false, reason: 'too-long' };
    if (s.issuedAt > expect.now + CLOCK_SKEW_MS)
        return { ok: false, reason: 'not-yet-valid' };
    if (s.expiresAt <= expect.now)
        return { ok: false, reason: 'expired' };
    if (!(await verifyStatement(pin.publicKey, PRESENCE_UNLOCK_DOMAIN, s, proof.signature)))
        return { ok: false, reason: 'bad-signature' };
    return { ok: true, statement: s };
}
//# sourceMappingURL=presence-unlock.js.map